import { FormSection } from '../../../components';
import { RangePicker } from '@oforce/global-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, CustomInput } from 'reactstrap';
import { useEffect, useState } from 'react';
import { useQuery, gql } from '@apollo/client';

const GET_POLICY = gql`
  query GetPolicy($id: ID!) {
    policy(id: $id) {
      id
      effectiveDate
      expirationDate
    }
  }
`;

const validateDates = (startDate, endDate, policy) => {
  if (!startDate || !endDate) {
    return null;
  }

  const today = new Date().toISOString().split('T')[0];

  if (startDate > today) {
    return 'Report start date cannot be in the future';
  }

  if (endDate > today) {
    return 'Report end date cannot be in the future';
  }

  if (policy) {
    if (policy.effectiveDate && startDate < policy.effectiveDate) {
      return `Report start date must be on or after policy effective date (${policy.effectiveDate})`;
    }

    if (policy.expirationDate && endDate > policy.expirationDate) {
      return `Report end date must be on or before policy expiration date (${policy.expirationDate})`;
    }
  }

  return null;
};

export default function ActivityReportDateSelection({ formik }) {
  const [validationError, setValidationError] = useState(null);
  const policyId = formik?.values?.policyId;

  const { data: policyData } = useQuery(GET_POLICY, {
    variables: { id: policyId },
    skip: !policyId
  });

  const policy = policyData?.policy;

  const setDateFilter = dates => {
    if (dates) {
      const [startDate, endDate] = dates;
      let reportStartDate = null;
      let reportEndDate = null;
      if (startDate) reportStartDate = startDate.format('YYYY-MM-DD');
      if (endDate) reportEndDate = endDate.format('YYYY-MM-DD');

      const error = validateDates(reportStartDate, reportEndDate, policy);
      setValidationError(error);

      formik?.setFieldValue('reportStartDate', reportStartDate);
      formik?.setFieldValue('reportEndDate', reportEndDate);
    } else {
      setValidationError(null);
      formik?.setFieldValue('reportStartDate', null);
      formik?.setFieldValue('reportEndDate', null);
    }
  };

  useEffect(() => {
    if (formik?.values?.reportStartDate && formik?.values?.reportEndDate) {
      const error = validateDates(formik.values.reportStartDate, formik.values.reportEndDate, policy);
      setValidationError(error);
    }
  }, [policy, formik?.values?.reportStartDate, formik?.values?.reportEndDate]);

  return (
    <FormSection title="Select Report Date Range">
      <div className="d-flex flex-column">
        <div className="d-flex flex-row flex-wrap mb-3">
          <DateRangeFilter onChange={setDateFilter} />
        </div>
        {validationError && (
          <Alert color="danger" className="mb-3">
            {validationError}
          </Alert>
        )}
        <CustomInput
          type="checkbox"
          id="includeComplianceIntervals"
          label="Include compliance interval details"
          checked={formik?.values?.includeComplianceIntervals || false}
          onChange={e => formik?.setFieldValue('includeComplianceIntervals', e.target.checked)}
          className="mb-2"
        />
        <Alert color="info" className="mb-0">
          Show granular compliance status changes for each exposure election over time
        </Alert>
      </div>
    </FormSection>
  );
}

const DateRangeFilter = ({ onChange }) => (
  <div className="d-flex flex-column mr-2 mb-3">
    <RangePicker
      style={{ minHeight: '38px' }}
      format="MM/DD/YYYY"
      onChange={onChange}
      separator={<FontAwesomeIcon icon={['far', 'long-arrow-alt-right']} className="text-gray-500 ml-0" />}
    />
  </div>
);
